import { Box, HStack, Icon, Text, Tooltip } from "@chakra-ui/react";
import { LuMail, LuMessageSquare, LuCalendar, LuClipboardCheck, LuFlag, LuUser } from "react-icons/lu";
import { ActionType } from "features/Resources/Shared/models";

type ActionIconTagType = ActionType | "person";
type ActionIconTagSize = "xs" | "sm" | "md";

interface ActionIconTagProps {
  actionType: ActionIconTagType;
  size?: ActionIconTagSize;
  showLabel?: boolean;
  withBox?: boolean;
}

// Custom size configurations for ActionIconTag
// Based on design specs: generous padding (12px), icon size 20px, text 10px
const ACTION_ICON_TAG_SIZES: Record<
  ActionIconTagSize,
  {
    px: string;
    py: string;
    iconSize: string;
    fontSize: string;
    gap: string;
  }
> = {
  xs: {
    px: "4px",
    py: "4px",
    iconSize: "14px",
    fontSize: "6px",
    gap: "1px",
  },
  sm: {
    px: "6px",
    py: "6px",
    iconSize: "16px",
    fontSize: "8px",
    gap: "2px",
  },
  md: {
    px: "10px",
    py: "10px",
    iconSize: "18px",
    fontSize: "10px",
    gap: "4px",
  },
};

const ACTION_CONFIGS = {
  email: { icon: LuMail, bg: "blue.50", color: "blue.500", label: "Email" },
  message: { icon: LuMessageSquare, bg: "green.50", color: "green.600", label: "Message" },
  task: { icon: LuClipboardCheck, bg: "teal.50", color: "teal.600", label: "Task" },
  meeting: { icon: LuCalendar, bg: "orange.50", color: "orange.500", label: "Meeting" },
  checkpoint: { icon: LuFlag, bg: "purple.50", color: "purple.500", label: "Checkpoint" },
  person: { icon: LuUser, bg: "gray.100", color: "gray.500", label: "Person" },
} as const;

// Base components

interface ActionIconBadgeProps {
  actionType: ActionIconTagType;
  size?: ActionIconTagSize;
}

interface ActionBadgeProps {
  actionType: ActionIconTagType;
  size?: ActionIconTagSize;
}

interface ActionIconBadgeWithCountProps {
  actionType: ActionIconTagType;
  count: number;
  size?: ActionIconTagSize;
}

interface ActionCountBadgeProps {
  actionType: ActionIconTagType;
  count: number;
  size?: ActionIconTagSize;
}

function ActionIconBadge({ actionType, size = "md" }: ActionIconBadgeProps) {
  const config = ACTION_CONFIGS[actionType];
  if (!config) return null;

  const styles = ACTION_ICON_TAG_SIZES[size];

  return (
    <Tooltip label={config.label} placement="top" hasArrow>
      <Box
        as="span"
        bg={config.bg}
        px={styles.px}
        py={styles.py}
        borderRadius="md"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
      >
        <Icon
          as={config.icon}
          color={config.color}
          boxSize={styles.iconSize}
          flexShrink={0}
          title={config.label}
          cursor="default"
        />
      </Box>
    </Tooltip>
  );
}

function ActionIconBadgeWithCount({ actionType, count, size = "md" }: ActionIconBadgeWithCountProps) {
  if (count <= 0) return null;

  const config = ACTION_CONFIGS[actionType];
  if (!config) return null;

  const styles = ACTION_ICON_TAG_SIZES[size];

  return (
    <Tooltip label={`${config.label} (${count})`} placement="top" hasArrow>
      <HStack
        spacing={styles.gap}
        bg={config.bg}
        color={config.color}
        px={styles.px}
        py={styles.py}
        borderRadius="md"
        display="inline-flex"
      >
        <Icon as={config.icon} boxSize={styles.iconSize} />
        <Text fontSize={styles.fontSize} fontWeight={600} lineHeight="15px">
          {count}
        </Text>
      </HStack>
    </Tooltip>
  );
}

function ActionBadge({ actionType, size = "md" }: ActionBadgeProps) {
  const config = ACTION_CONFIGS[actionType];
  if (!config) return null;

  const styles = ACTION_ICON_TAG_SIZES[size];

  return (
    <HStack
      spacing={styles.gap}
      bg={config.bg}
      color={config.color}
      px={styles.px}
      py={styles.py}
      borderRadius="md"
      display="inline-flex"
    >
      <Icon as={config.icon} boxSize={styles.iconSize} />
      <Text fontSize={styles.fontSize} fontWeight={600} lineHeight="15px">
        {config.label}
      </Text>
    </HStack>
  );
}

function ActionCountBadge({ actionType, count, size = "md" }: ActionCountBadgeProps) {
  if (count <= 0) return null;

  const config = ACTION_CONFIGS[actionType];
  if (!config) return null;

  const styles = ACTION_ICON_TAG_SIZES[size];

  return (
    <HStack
      spacing={styles.gap}
      bg={config.bg}
      color={config.color}
      px={styles.px}
      py={styles.py}
      borderRadius="md"
      display="inline-flex"
    >
      <Icon as={config.icon} boxSize={styles.iconSize} />
      <Text fontSize={styles.fontSize} fontWeight={600} lineHeight="15px">
        {count} {config.label}
      </Text>
    </HStack>
  );
}

// Checkpoint
function CheckpointIconBadge({ size }: { size?: ActionIconTagSize }) {
  return <ActionIconBadge actionType="checkpoint" size={size} />;
}

function CheckpointIconBadgeWithCount({ count, size }: { count: number; size?: ActionIconTagSize }) {
  return <ActionIconBadgeWithCount actionType="checkpoint" count={count} size={size} />;
}

function CheckpointBadge({ size }: { size?: ActionIconTagSize }) {
  return <ActionBadge actionType="checkpoint" size={size} />;
}

function CheckpointCountBadge({ count, size }: { count: number; size?: ActionIconTagSize }) {
  return <ActionCountBadge actionType="checkpoint" count={count} size={size} />;
}

// Email
function EmailIconBadge({ size }: { size?: ActionIconTagSize }) {
  return <ActionIconBadge actionType="email" size={size} />;
}

function EmailIconBadgeWithCount({ count, size }: { count: number; size?: ActionIconTagSize }) {
  return <ActionIconBadgeWithCount actionType="email" count={count} size={size} />;
}

function EmailBadge({ size }: { size?: ActionIconTagSize }) {
  return <ActionBadge actionType="email" size={size} />;
}

function EmailCountBadge({ count, size }: { count: number; size?: ActionIconTagSize }) {
  return <ActionCountBadge actionType="email" count={count} size={size} />;
}

// Message
function MessageIconBadge({ size }: { size?: ActionIconTagSize }) {
  return <ActionIconBadge actionType="message" size={size} />;
}

function MessageIconBadgeWithCount({ count, size }: { count: number; size?: ActionIconTagSize }) {
  return <ActionIconBadgeWithCount actionType="message" count={count} size={size} />;
}

function MessageBadge({ size }: { size?: ActionIconTagSize }) {
  return <ActionBadge actionType="message" size={size} />;
}

function MessageCountBadge({ count, size }: { count: number; size?: ActionIconTagSize }) {
  return <ActionCountBadge actionType="message" count={count} size={size} />;
}

// Meeting
function MeetingIconBadge({ size }: { size?: ActionIconTagSize }) {
  return <ActionIconBadge actionType="meeting" size={size} />;
}

function MeetingIconBadgeWithCount({ count, size }: { count: number; size?: ActionIconTagSize }) {
  return <ActionIconBadgeWithCount actionType="meeting" count={count} size={size} />;
}

function MeetingBadge({ size }: { size?: ActionIconTagSize }) {
  return <ActionBadge actionType="meeting" size={size} />;
}

function MeetingCountBadge({ count, size }: { count: number; size?: ActionIconTagSize }) {
  return <ActionCountBadge actionType="meeting" count={count} size={size} />;
}

// Task
function TaskIconBadge({ size }: { size?: ActionIconTagSize }) {
  return <ActionIconBadge actionType="task" size={size} />;
}

function TaskIconBadgeWithCount({ count, size }: { count: number; size?: ActionIconTagSize }) {
  return <ActionIconBadgeWithCount actionType="task" count={count} size={size} />;
}

function TaskBadge({ size }: { size?: ActionIconTagSize }) {
  return <ActionBadge actionType="task" size={size} />;
}

function TaskCountBadge({ count, size }: { count: number; size?: ActionIconTagSize }) {
  return <ActionCountBadge actionType="task" count={count} size={size} />;
}

// Person
function PersonIconBadge({ size }: { size?: ActionIconTagSize }) {
  return <ActionIconBadge actionType="person" size={size} />;
}

function PersonIconBadgeWithCount({ count, size }: { count: number; size?: ActionIconTagSize }) {
  return <ActionIconBadgeWithCount actionType="person" count={count} size={size} />;
}

function PersonBadge({ size }: { size?: ActionIconTagSize }) {
  return <ActionBadge actionType="person" size={size} />;
}

function PersonCountBadge({ count, size }: { count: number; size?: ActionIconTagSize }) {
  return <ActionCountBadge actionType="person" count={count} size={size} />;
}

// Dynamic component for runtime action type determination
function DynamicActionIconTag({ actionType, size = "md", showLabel = false, withBox = true }: ActionIconTagProps) {
  const config = ACTION_CONFIGS[actionType];

  if (!config) {
    return null;
  }

  const styles = ACTION_ICON_TAG_SIZES[size];

  if (showLabel) {
    return <ActionBadge actionType={actionType} size={size} />;
  }

  if (!withBox) {
    return (
      <Tooltip label={config.label} placement="top" hasArrow>
        <Box as="span" display="inline-flex">
          <Icon
            as={config.icon}
            color={config.color}
            boxSize={styles.iconSize}
            flexShrink={0}
            title={config.label}
            cursor="default"
          />
        </Box>
      </Tooltip>
    );
  }

  return <ActionIconBadge actionType={actionType} size={size} />;
}

// Public API
export const ActionIconTag = {
  // Checkpoint
  CheckpointIconBadge,
  CheckpointIconBadgeWithCount,
  CheckpointBadge,
  CheckpointCountBadge,
  // Email
  EmailIconBadge,
  EmailIconBadgeWithCount,
  EmailBadge,
  EmailCountBadge,
  // Message
  MessageIconBadge,
  MessageIconBadgeWithCount,
  MessageBadge,
  MessageCountBadge,
  // Meeting
  MeetingIconBadge,
  MeetingIconBadgeWithCount,
  MeetingBadge,
  MeetingCountBadge,
  // Task
  TaskIconBadge,
  TaskIconBadgeWithCount,
  TaskBadge,
  TaskCountBadge,
  // Person
  PersonIconBadge,
  PersonIconBadgeWithCount,
  PersonBadge,
  PersonCountBadge,
  // Dynamic component for runtime action type determination
  Icon: DynamicActionIconTag,
};
